import React from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { X } from "lucide-react";

import { ModalDownloadUploadCstInterface, StatusEnum } from "./types";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import { ButtonSecondary } from "src/UI/ButtonSecondary";
import { ButtonTertiary } from "src/UI/ButtonTertiary";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const ModalConfirmsCst: React.FC<ModalDownloadUploadCstInterface> = ({
  open,
  title,
  description,
  onCallBackClosed,
  onActionConfirmed,
  status,
  costumMessage,
  routeToSamePage = false,
  children,
  navigateUrl,
  removeButtons = false,
  callBackConfirmed,
  successActionLabel,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation(["modal"]);
  const theme = useTheme();

  const handleNavigate = () => {
    if (!!callBackConfirmed) {
      callBackConfirmed();
    } else {
      if (navigateUrl) {
        navigate(navigateUrl);
      } else {
        navigate(-1);
      }
    }
  };

  const statusContentSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: "16px",
    width: "100%",
  } as const;

  const renderStatus = React.useMemo(() => {
    if (status) {
      switch (status.s) {
        case StatusEnum.OK: {
          return (
            <Box sx={statusContentSx}>
              <CheckCircleOutlineIcon
                sx={{ fontSize: "60px", color: theme.palette.accent.main }}
              />
              <Typography variant="h5" component={"p"}>
                {status?.title
                  ? status?.title
                  : t("successfully-submit-button")}
              </Typography>
              {status.successMessage ? <>{status.successMessage}</> : <></>}
              {status.message && (
                <Typography variant="h6" component={"p"}>
                  {status.message}
                </Typography>
              )}
              {status.customContent ? status.customContent : ""}
              {status.child}
            </Box>
          );
        }
        case StatusEnum.KO: {
          return (
            <Box sx={statusContentSx}>
              <ErrorOutlineIcon
                sx={{ fontSize: "60px", color: theme.palette.error.main }}
              />
              <Typography variant="h5" component={"p"}>
                {status?.title ? status?.title : t("error-submit-button")}
              </Typography>
              {status.message && (
                <Typography
                  variant="h6"
                  component={"p"}
                  sx={{
                    wordWrap: "break-word",
                    width: "100%",
                    whiteSpace: "pre-line",
                  }}
                >
                  {status.message}
                </Typography>
              )}
            </Box>
          );
        }
        case StatusEnum.LOADING: {
          return (
            <Box sx={statusContentSx}>
              <CircularProgress
                size={60}
                sx={{
                  marginRight: "5px",
                  width: "20px",
                  height: "20px",
                }}
              />
              <Typography variant="h5" component={"p"}>
                {t("loading-text")}
              </Typography>
            </Box>
          );
        }
        case StatusEnum.CUSTOM: {
          return (
            <Box sx={statusContentSx}>
              {status.customContent ? status.customContent : ""}
              {!removeButtons && (
                <>
                  <Divider flexItem />
                  <ButtonPrimary
                    id={"btn-navigate"}
                    size="md"
                    onClick={
                      routeToSamePage ? onCallBackClosed : handleNavigate
                    }
                    sx={{ width: "100%" }}
                  >
                    OK
                  </ButtonPrimary>
                </>
              )}
            </Box>
          );
        }
      }
    }
  }, [status]);

  const showHeader = !status || (status && StatusEnum.KO.includes(status.s));
  // La X serve solo quando non c'è il pulsante "Annulla" (stato KO o removeButtons)
  const showCloseIcon = showHeader && (!!status || removeButtons);
  const successActionText =
    successActionLabel ??
    t(routeToSamePage ? "outcome-close-button" : "outcome-back-button");

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth={false}
      aria-labelledby="modal-confirms-title"
      PaperProps={{
        sx: {
          maxWidth: "800px",
          margin: "16px",
          backgroundColor: theme.palette.backgroundCard,
          border: `2px solid ${theme.palette.border}`,
          borderRadius: "16px",
          backgroundImage: "none",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: "24px",
          padding: "32px",
          color: theme.palette.text.primary,
        }}
      >
        {showCloseIcon && (
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}
          >
            <IconButton
              onClick={() => onCallBackClosed()}
              aria-label="close"
              sx={{ padding: 0, color: theme.palette.text.primary }}
            >
              <X size={24} />
            </IconButton>
          </Box>
        )}
        {showHeader && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "16px",
              width: "100%",
            }}
          >
            <Typography
              id="modal-confirms-title"
              component="h2"
              sx={{
                fontSize: "24px",
                fontWeight: 700,
                lineHeight: 1.2,
                overflowWrap: "anywhere",
              }}
            >
              {title}
            </Typography>
            {!status && (
              <Typography
                component="p"
                sx={{ fontSize: "16px", fontWeight: 400, lineHeight: 1.5 }}
              >
                {description}
              </Typography>
            )}
            {!status && costumMessage && (
              <Typography
                component="p"
                sx={{ fontSize: "16px", fontWeight: 400, lineHeight: 1.5 }}
              >
                {costumMessage}
              </Typography>
            )}
          </Box>
        )}
        {children}
        {renderStatus}
        {!status && !removeButtons && (
          <>
            <Divider flexItem />
            <Box sx={{ display: "flex", gap: "12px", width: "100%" }}>
              <ButtonSecondary
                id={"btn-closed"}
                size="md"
                onClick={onCallBackClosed}
                sx={{ flex: 1 }}
              >
                {t("cancel")}
              </ButtonSecondary>
              <ButtonPrimary
                id={"btn-confirm"}
                size="md"
                onClick={onActionConfirmed}
                sx={{ flex: 1 }}
              >
                {t("confirm")}
              </ButtonPrimary>
            </Box>
          </>
        )}
        {status && StatusEnum.OK.includes(status.s) && (
          <>
            <Divider flexItem />
            <ButtonTertiary
              id={"btn-navigate"}
              size="md"
              onClick={routeToSamePage ? onCallBackClosed : handleNavigate}
            >
              {successActionText}
            </ButtonTertiary>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default ModalConfirmsCst;
